'use client';

import { useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useTranslations } from 'next-intl';
import { HpApiWebApiError } from '@zg-rentals/ts-hotpads-api-client';
import type { HpWebListingFilterDto } from '@zg-rentals/ts-hotpads-api-client';
import type { AreaResponse } from '@/src/lib/api';
import { v3ErrorReason } from '@/src/lib/api/client/hotpadsApiV3Client';
import { useFeatureFlag } from '@/src/lib/feature-flags';
import {
  useCreateInquiry,
  useCurrentArea,
  useCurrentUser,
  useInquiryMessageCache,
  userListingKeys,
  useSendOtp,
} from '@/src/lib/hooks/query';
import { inquiryKeys } from '@/src/lib/hooks/query/queryKeys';
import { useOptionalFilters } from '@/src/lib/filters';
import { DEFAULT_FILTERS, type FilterState } from '@/src/lib/filters/filterTypes';
import { filterStateToV3Filter } from '@/src/lib/filters/filterStateToV3';
import { toSearchScope } from '@/src/lib/filters/searchScope';
import { getActiveSearchHdpBackPath } from '@/src/lib/navigation/searchHdpHistory';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { useSimilarListings } from '@/src/components/features/property/hooks/useSimilarListings';
import type { SimilarListing } from '@/src/components/features/property/hooks/useSimilarListings';
import { analytics } from '@/src/components/features/property/lib';
import { getContactFormClickstreamContext } from '@/src/components/features/property/lib/analytics';
import { isWaitlistedListing } from '@/src/components/features/property/lib/waitlist';
import { hasSeenRenterProfilePrompt, markRenterProfilePromptShown } from './renterProfilePromptTracking';
import type {
  ContactClickstreamContext,
  ContactFormHostMode,
  ContactFormInputValues,
  ContactFormMode,
  ContactFormSavedSearchContext,
  ContactFormServerErrors,
  ContactSubmissionContext,
  OtpSignInStarted,
} from './types';

type UseHdpContactSubmissionOptions = {
  listing?: PropertyDetailListing | null;
  mode: ContactFormMode;
  formMode: ContactFormHostMode;
  clickstreamContext?: ContactClickstreamContext;
};

type ParsedSearchPath = {
  resourceId: string;
  searchSlug: string;
};

/**
 * The form values and submission id held while the OTP step is up, so verifying can finish
 * a send the renter already asked for without them re-entering anything — and without
 * re-firing the analytics that went out when they pressed the CTA.
 */
type PendingInquiry = {
  values: ContactFormInputValues;
  rentalSubmitId: string;
};

const DEFAULT_POST_CONTACT_SEARCH_SLUG = 'apartments-for-rent';

// Exhausts the address's send budget, but a code already in the inbox stays redeemable —
// OtpSignInForm blocks further sends without locking verification, so these advance the step
// rather than failing the submit.
const SEND_LIMIT_REASONS = new Set<HpApiWebApiError>([
  HpApiWebApiError.SEND_LIMIT_EXCEEDED,
  HpApiWebApiError.TOO_MANY_REQUESTS,
]);

const DEFAULT_SERVER_ERRORS: ContactFormServerErrors = {
  emailError: '',
  phoneError: '',
  formError: '',
};

function formatPhoneForInquiry(raw: string) {
  const digits = raw.replace(/\D/g, '');
  if (digits.length !== 10) return raw;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function createRentalSubmitId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function parseSearchPath(searchPath: string | null): ParsedSearchPath | null {
  const path = searchPath?.split(/[?#]/)[0];
  const segments = path?.split('/').filter(Boolean) ?? [];
  if (segments.length < 2) return null;

  const [resourceId, searchSlug] = segments;
  if (!resourceId || !searchSlug) return null;
  if (searchSlug === 'pad' || searchSlug === 'building') return null;

  return { resourceId, searchSlug };
}

function hasUsableBounds(area: AreaResponse | undefined): area is AreaResponse & {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
} {
  return (
    typeof area?.minLat === 'number' &&
    typeof area.maxLat === 'number' &&
    typeof area.minLon === 'number' &&
    typeof area.maxLon === 'number'
  );
}

function getSavedSearchContext(
  area: AreaResponse | undefined,
  parsedSearchPath: ParsedSearchPath | null,
  filters: FilterState,
): ContactFormSavedSearchContext | null {
  const areaId = area?.id ?? area?.resourceId;
  const locationText = area?.name ?? area?.city ?? area?.resourceId;
  const searchPath =
    parsedSearchPath ??
    (area?.resourceId
      ? {
          resourceId: area.resourceId,
          searchSlug: DEFAULT_POST_CONTACT_SEARCH_SLUG,
        }
      : null);

  if (!areaId || !locationText || !searchPath || !hasUsableBounds(area)) {
    return null;
  }

  return {
    areaId,
    resourceId: searchPath.resourceId,
    searchSlug: searchPath.searchSlug,
    locationText,
    areaBounds: {
      minLat: area.minLat,
      maxLat: area.maxLat,
      minLon: area.minLon,
      maxLon: area.maxLon,
    },
    filters,
    searchScope: toSearchScope(area),
  };
}

/**
 * The search context the inquiry is sent from. v3 `create` only decides the saved-search
 * prompt when the request carries one, so this replaces the standalone
 * `displaySearchPromptPostInquiry` call the prompt used to come from.
 */
function buildInquirySearchFilter(
  savedSearchContext: ContactFormSavedSearchContext | null,
): HpWebListingFilterDto | undefined {
  if (!savedSearchContext) return undefined;

  // The DTO types areaId as a number, but HotPads area ids can exceed MAX_SAFE_INTEGER —
  // sending a rounded id would clip against the wrong area, so drop it and let the
  // bounding box govern.
  const areaId = Number(savedSearchContext.areaId);
  const filter = filterStateToV3Filter(
    savedSearchContext.filters,
    savedSearchContext.areaBounds,
    Number.isSafeInteger(areaId) ? areaId : undefined,
    savedSearchContext.searchScope,
  );

  // filterStateToV3Filter models the search request as PartialDeep and omits empty flag
  // lists; the inquiry DTO requires both arrays. Widen once, here at the boundary.
  return {
    ...filter,
    mustHave: filter.mustHave ?? [],
    mustNotHave: filter.mustNotHave ?? [],
  } as HpWebListingFilterDto;
}

function getAvailableSimilarListings(
  similarListings: Array<SimilarListing> | undefined,
  listing: PropertyDetailListing,
) {
  const listingAlias = listing.aliasEncoded;
  const listingMaloneLotId = listing.maloneLotIdEncoded;

  return (similarListings ?? [])
    .filter((similarListing) => {
      const aliasMatches = Boolean(listingAlias) && similarListing.aliasEncoded === listingAlias;
      const lotMatches =
        Boolean(listingMaloneLotId) &&
        (similarListing.maloneLotIdEncoded === listingMaloneLotId ||
          similarListing.lotIdEncoded === listingMaloneLotId);

      return Boolean(similarListing.aliasEncoded) && !aliasMatches && !lotMatches;
    })
    .slice(0, 5);
}

function getInquiryText(values: ContactFormInputValues) {
  return [values.userMessage, values.lowIncomeMessage]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join('\n\n');
}

export function useHdpContactSubmission({
  listing,
  mode,
  formMode,
  clickstreamContext,
}: UseHdpContactSubmissionOptions) {
  const t = useTranslations('contactForm');
  const queryClient = useQueryClient();
  const filterContext = useOptionalFilters();
  const filters = filterContext?.filters ?? DEFAULT_FILTERS;
  const mockInquiryEnabled = useFeatureFlag('mock-inquiry');
  const { data: currentUser } = useCurrentUser();
  const { data: currentArea } = useCurrentArea();
  const { data: similarListings } = useSimilarListings({
    maloneLotIdEncoded: listing?.maloneLotIdEncoded,
    unit: listing?.unit,
  });
  const { data: inquiryMessageCache } = useInquiryMessageCache();
  const createInquiry = useCreateInquiry();
  const sendOtp = useSendOtp();
  const [serverErrors, setServerErrors] = useState<ContactFormServerErrors>(DEFAULT_SERVER_ERRORS);
  // A ref, not state: the buffer is read once on resume and must not trigger a render of
  // the form sitting behind the OTP step.
  const pendingInquiryRef = useRef<PendingInquiry | null>(null);
  const resolvedClickstreamContext = useMemo<ContactClickstreamContext>(
    () => ({
      triggerLocation: clickstreamContext?.triggerLocation ?? 'home_details_halo',
      triggerObject:
        formMode === 'inline'
          ? 'home_details_component|inline_form'
          : (clickstreamContext?.triggerObject ?? 'home_details_component|summary'),
    }),
    [clickstreamContext?.triggerLocation, clickstreamContext?.triggerObject, formMode],
  );
  const submitClickstreamContext = getContactFormClickstreamContext(resolvedClickstreamContext, 'submit', formMode);

  const savedSearchContext = useMemo(() => {
    const parsedSearchPath = parseSearchPath(getActiveSearchHdpBackPath());
    return getSavedSearchContext(currentArea, parsedSearchPath, filters);
  }, [currentArea, filters]);

  const searchFilter = useMemo(() => buildInquirySearchFilter(savedSearchContext), [savedSearchContext]);

  const isLoading = !mockInquiryEnabled && (currentUser === undefined || sendOtp.isPending || createInquiry.isPending);

  // Three states, not two: `undefined` is identity still resolving, `null` is a resolved
  // signed-out visitor, and an object is a resolved session. Only the middle one needs a
  // code — testing `?.loggedIn === false` would miss `null`, which is what a signed-out
  // visitor actually gets, and send them straight to a create that 401s.
  const requiresSignIn = !mockInquiryEnabled && currentUser !== undefined && !currentUser?.loggedIn;

  const serverErrorsForInquiryFailure = (error: unknown): ContactFormServerErrors => {
    switch (v3ErrorReason(error)) {
      case HpApiWebApiError.INVALID_PHONE:
        return { ...DEFAULT_SERVER_ERRORS, phoneError: t('errorContactValidation') };
      case HpApiWebApiError.INVALID_EMAIL:
        return { ...DEFAULT_SERVER_ERRORS, emailError: t('errorContactValidation') };
      case HpApiWebApiError.ACCOUNT_DEACTIVATED:
        return { ...DEFAULT_SERVER_ERRORS, emailError: t('errorAccountDeactivated') };
      default:
        return { ...DEFAULT_SERVER_ERRORS, formError: t('errorSubmit') };
    }
  };

  const serverErrorsForSendOtpFailure = (error: unknown): ContactFormServerErrors => {
    switch (v3ErrorReason(error)) {
      case HpApiWebApiError.INVALID_EMAIL:
        return { ...DEFAULT_SERVER_ERRORS, emailError: t('errorEmailInvalid') };
      case HpApiWebApiError.ACCOUNT_DEACTIVATED:
        return { ...DEFAULT_SERVER_ERRORS, emailError: t('errorAccountDeactivated') };
      // Both OTP endpoints 404 while the emailOtpSettings.v3EndpointsEnabled kill switch is off.
      case HpApiWebApiError.NOT_FOUND:
        return { ...DEFAULT_SERVER_ERRORS, formError: t('errorSignInUnavailable') };
      default:
        return { ...DEFAULT_SERVER_ERRORS, formError: t('errorSignInFailed') };
    }
  };

  const sendInquiry = async (
    values: ContactFormInputValues,
    rentalSubmitId: string,
    isLoggedIn: boolean,
  ): Promise<ContactSubmissionContext | null> => {
    if (!listing?.aliasEncoded) {
      setServerErrors((prev) => ({ ...prev, formError: t('errorSubmit') }));
      return null;
    }

    const text = getInquiryText(values);
    const formattedPhone = formatPhoneForInquiry(values.userPhoneNumber);

    try {
      let displaySavedSearchPrompt = mockInquiryEnabled;

      if (!mockInquiryEnabled) {
        // No email field: v3 takes the inquirer address from the authenticated session,
        // which is what makes the sign-in gate above load-bearing.
        const response = await createInquiry.mutateAsync({
          postData: {
            listingAlias: listing.aliasEncoded,
            name: values.userName,
            phone: formattedPhone,
            text,
            rentalSubmitId,
            requestToApply: mode === 'requestToApply',
            ...(values.numBeds ? { beds: values.numBeds } : {}),
            ...(values.numBaths ? { baths: values.numBaths } : {}),
            ...(values.moveDate ? { moveDate: values.moveDate } : {}),
            ...(searchFilter ? { searchFilter } : {}),
          },
        });

        displaySavedSearchPrompt = Boolean(response.data?.displaySavedSearchPrompt);
      }

      queryClient.setQueryData(inquiryKeys.messageCache(), {
        email: values.userEmail,
        name: values.userName,
        phone: formattedPhone,
        text: values.userMessage,
      });
      queryClient.invalidateQueries({ queryKey: userListingKeys.all });

      const shouldShowRenterProfile = isLoggedIn && !hasSeenRenterProfilePrompt();
      if (shouldShowRenterProfile) {
        markRenterProfilePromptShown();
      }

      return {
        listing,
        mode,
        formMode,
        clickstreamContext: resolvedClickstreamContext,
        inquiry: {
          name: values.userName,
          email: values.userEmail,
          phone: formattedPhone,
          text,
        },
        isLoggedIn,
        isWaitlist: isWaitlistedListing(listing),
        shouldShowRenterProfile,
        shouldShowSavedSearch: Boolean(displaySavedSearchPrompt && savedSearchContext),
        savedSearchContext,
        similarListings: getAvailableSimilarListings(similarListings, listing),
      };
    } catch (error) {
      setServerErrors(serverErrorsForInquiryFailure(error));
      return null;
    }
  };

  /**
   * Opens a submission: mints the id the analytics and the inquiry must share, and fires the
   * lead events. HP-8576 legacy parity puts those before any network write, and they fire
   * exactly once per submission — the send resumed after sign-in reuses this id rather than
   * emitting a second pair for the same lead. Returns null when the submission cannot start.
   */
  const openSubmission = (): string | null => {
    // A second press while the first is in flight is a duplicate lead, not a retry.
    if (sendOtp.isPending || createInquiry.isPending) return null;

    setServerErrors(DEFAULT_SERVER_ERRORS);

    if (!mockInquiryEnabled && currentUser === undefined) return null;

    if (!listing?.aliasEncoded) {
      setServerErrors((prev) => ({ ...prev, formError: t('errorSubmit') }));
      return null;
    }

    const rentalSubmitId = createRentalSubmitId();

    analytics.emitTrackContactClickEvent({
      ...submitClickstreamContext,
      listing,
      mode,
      phase: 'submit',
      rentalSubmitId,
    });

    analytics.emitTrackEmailLeadEvent({
      listing,
      ...submitClickstreamContext,
      triggerSource:
        mode === 'requestToApply' ? 'button_to_complete_request_to_apply_form' : 'button_to_complete_contact_form',
    });

    return rentalSubmitId;
  };

  /**
   * Satisfies the sign-in prerequisite: buffers the inquiry and sends the first code, so the
   * caller only has to show the step. The step deliberately does not send that code itself —
   * an RQ5 mutate() fired from a mount effect is orphaned under StrictMode.
   *
   * Returns null when no code could be sent and none is redeemable.
   */
  const startOtpSignIn = async (values: ContactFormInputValues): Promise<OtpSignInStarted | null> => {
    const rentalSubmitId = openSubmission();
    if (!rentalSubmitId) return null;

    let sendLimitReached = false;

    try {
      await sendOtp.mutateAsync({ postData: { email: values.userEmail } });
    } catch (error) {
      const reason = v3ErrorReason(error);
      if (!reason || !SEND_LIMIT_REASONS.has(reason)) {
        setServerErrors(serverErrorsForSendOtpFailure(error));
        return null;
      }
      // No new code is coming, but an earlier one may still be sitting in their inbox.
      sendLimitReached = true;
    }

    pendingInquiryRef.current = { values, rentalSubmitId };
    return { email: values.userEmail, sendLimitReached };
  };

  /** Sends the inquiry for a session that already satisfies the sign-in prerequisite. */
  const submitInquiry = async (values: ContactFormInputValues): Promise<ContactSubmissionContext | null> => {
    const rentalSubmitId = openSubmission();
    if (!rentalSubmitId) return null;

    return sendInquiry(values, rentalSubmitId, Boolean(currentUser?.loggedIn));
  };

  /**
   * Finishes the send the renter started before signing in. Returns null when the inquiry
   * itself is rejected (an invalid phone, say) — the caller returns them to the form with
   * their data intact, and a resubmit needs no second code because the session is live now.
   */
  const resumeAfterSignIn = async (): Promise<ContactSubmissionContext | null> => {
    const pendingInquiry = pendingInquiryRef.current;
    pendingInquiryRef.current = null;
    if (!pendingInquiry) return null;

    return sendInquiry(pendingInquiry.values, pendingInquiry.rentalSubmitId, true);
  };

  /** Abandons a buffered inquiry without sending it. */
  const cancelPendingInquiry = () => {
    pendingInquiryRef.current = null;
  };

  return {
    cancelPendingInquiry,
    inquiryMessageCache,
    isLoading,
    requiresSignIn,
    resumeAfterSignIn,
    serverErrors,
    startOtpSignIn,
    submitInquiry,
  };
}
